import { VerityInstituteSettings } from "../../types/common";
import ArrowLeft from "../images/arrowLeft.svg?react";
import ArrowRight from "../images/arrowRight.svg?react";
import styles from "./proctoringOptionsGrid.module.css";
import { useEffect, useState, useRef } from "react";
import { List, ListItem } from "@mui/material";
import { CoreButton } from "../../core";
import { Application, Extension, ProctoringOption, ProctoringPlatforms } from "@verity/proctoring-option/types";
import { PROCTORING_OPTION_TEXT, ProctoringOptionCategory } from "./option-text";
import type { VerityApi } from "../../api/fetch";
import OtherActivitiesOptions from "./option-sections/other-activities-options";
import ShowToolsOptions from "./option-sections/show-tools-options";
import BlockItemsOptions from "./option-sections/restrict-applications-options";
import DisableFeaturesOptions from "./option-sections/disable-features-options";
import TabsOptions from "./option-sections/tab-options";
import VerifyIdentityOptions from "./option-sections/verify-identity-options";
import RecordOptions from "./option-sections/record-options";
import i18nPackage from "../../i18n/i18n";

export type ProctoringOptionsProps<T extends Partial<ProctoringOption>> = {
	option: T;
	updateOption: (options: T) => void;
	disabled?: boolean;
	shouldShowOption: (option: keyof T) => boolean;
	isOptionAllowed: <O extends keyof T>(option: O, value?: T[O]) => boolean;
	getPartiallySupportedPlatforms: <O extends keyof T>(option: O, value?: T[O]) => Set<keyof ProctoringPlatforms>;
	applications: Application[];
	allowedCategories: ProctoringOptionCategory[];
	currentCategory: number;
	setCurrentCategory: React.Dispatch<React.SetStateAction<number>>;
	hasPassword?: boolean;
	setBodyRef?: (el: HTMLDivElement | null) => void;
};

export type OptionProps<T> = {
	option: T;
	updateOption: (options: T) => void;
	disabled?: boolean;
	isOptionAllowed: <O extends keyof T>(option: O, value?: T[O]) => boolean;
	getPartiallySupportedPlatforms?: <O extends keyof T>(option: O, value?: T[O]) => Set<keyof ProctoringPlatforms>;
	horizontalOrientation?: boolean;
};

export type CategoryOptionsListProps<T extends Partial<ProctoringOption>> = {
	option: T;
	updateOption: (options: T) => void;
	shouldShowOption: (option: keyof T) => boolean;
	isOptionAllowed: <O extends keyof T>(option: O, value?: T[O]) => boolean;
	getPartiallySupportedPlatforms: <O extends keyof T>(option: O, value?: T[O]) => Set<keyof ProctoringPlatforms>;
	disabled?: boolean;
	hasPassword?: boolean;
	horizontalOrientation?: boolean;
};

export function isValidPlatform<T extends Partial<ProctoringOption>>({
	option,
	verityInstituteSettings,
}: {
	option?: Pick<T, "isExtension" | "isLockdown" | "isMobile">;
	verityInstituteSettings?: VerityInstituteSettings;
}) {
	return Boolean(
		option &&
		verityInstituteSettings &&
		((option.isExtension && verityInstituteSettings.allowExtensionPlatform) ||
			(option.isLockdown && verityInstituteSettings.allowLockdownPlatform) ||
			(option.isMobile && verityInstituteSettings.allowMobilePlatform)),
	);
}

export function ProctoringOptions<T extends Partial<ProctoringOption>>({
	option,
	updateOption,
	disabled,
	shouldShowOption,
	getPartiallySupportedPlatforms,
	isOptionAllowed,
	applications,
	fetchChromeExtensionData,
	extensions,
	allowedCategories,
	currentCategory,
	setCurrentCategory,
	hasPassword,
	setBodyRef,
	instituteUuid,
	getInstituteSettings,
	courseId,
}: ProctoringOptionsProps<T> & {
	applications: Application[];
	extensions: Extension[];
	instituteUuid?: string;
	getInstituteSettings?: VerityApi["getInstituteSettings"];
	fetchChromeExtensionData: (id: string) => Promise<Extension | null>;
	courseId?: number;
}) {
	const itemsPerPage = 7;
	const [currentPage, setCurrentPage] = useState(0);
	const totalPages = Math.ceil(allowedCategories.length / itemsPerPage);
	const optionBodyRef = useRef<HTMLDivElement>(null); // for categories in configuration step, so after next is clicked, focus goes to body

	useEffect(() => {
		if (setBodyRef) setBodyRef(optionBodyRef.current);
	}, [setBodyRef]);

	// On first render (every function call), set current category to 0
	useEffect(() => {
		setCurrentCategory(0);
	}, []);

	const handleListItemClick = (index: number) => {
		setCurrentCategory(index);
	};

	const handleNext = () => {
		setCurrentPage((prev) => (prev + 1) % totalPages);
	};

	const handleBack = () => {
		setCurrentPage((prev) => (prev - 1 + totalPages) % totalPages);
	};

	const startIndex = currentPage * itemsPerPage;
	const displayedItems = allowedCategories.slice(startIndex, startIndex + itemsPerPage);

	return (
		<div className={styles.listWrapper}>
			<div className={styles.contentList}>
				<List
					component="nav"
					aria-label="proctoring options"
					sx={{ padding: "0px", height: "100%", border: "1px solid #ccc", borderBottom: "none" }}
				>
					{displayedItems.map((category, index) => (
						<ListItem
							button
							selected={currentCategory === startIndex + index}
							onClick={() => handleListItemClick(startIndex + index)}
							key={index}
							sx={{
								height: `${360 / displayedItems.length}px`,
								borderBottom: "1px solid #ccc",
								"&.Mui-selected, &.Mui-selected:hover": {
									backgroundColor: "#E7F1F3",
								},
								"&:focus-visible": {
									boxShadow: "0 0 0 2px var(--focus-outline-color-dark)", // Using box shadow instead of outline because of weird alignment issues
									zIndex: 1,
									backgroundColor: "transparent", // Override MUI default gray background
								},
								"&:hover": {
									backgroundColor: "#f0f0f0",
								},
							}}
						>
							<div className={styles.contentListItem}>{`${startIndex + index + 1}. ${category}`}</div>
						</ListItem>
					))}
				</List>
				{allowedCategories.length > 7 && (
					<div className={styles.rowView}>
						<CoreButton
							onClick={handleBack}
							disabled={currentPage === 0}
							sx={{
								border: "1px solid #ccc",
								borderRadius: "5px",
								padding: "0px",
								minWidth: "15px",
								minHeight: "15px",
							}}
						>
							<ArrowLeft />
						</CoreButton>
						<CoreButton
							onClick={handleNext}
							disabled={currentPage === totalPages - 1}
							sx={{
								border: "1px solid #ccc",
								borderRadius: "5px",
								padding: "0px",
								minWidth: "15px",
								minHeight: "15px",
							}}
						>
							<ArrowRight />
						</CoreButton>
					</div>
				)}
			</div>
			<div className={styles.optionList} ref={optionBodyRef} tabIndex={-1}>
				{Object.entries(PROCTORING_OPTION_TEXT)
					.filter(([, { settingDescription }]) => settingDescription)
					.every(([o]) => !isOptionAllowed(o as keyof ProctoringOption)) && (
					<div className={styles.errorMessage}>
						{i18nPackage.t("editQuizPage.configurationStep.allOptionsDisabled")}
					</div>
				)}

				{allowedCategories[currentCategory] === "Record" && (
					<RecordOptions
						option={option}
						updateOption={updateOption}
						disabled={disabled}
						shouldShowOption={shouldShowOption}
						isOptionAllowed={isOptionAllowed}
						instituteUuid={instituteUuid}
						getInstituteSettings={getInstituteSettings}
						getPartiallySupportedPlatforms={getPartiallySupportedPlatforms}
					/>
				)}

				{allowedCategories[currentCategory] === "Other Activities" && (
					<OtherActivitiesOptions
						option={option}
						updateOption={updateOption}
						disabled={disabled}
						shouldShowOption={shouldShowOption}
						isOptionAllowed={isOptionAllowed}
						getPartiallySupportedPlatforms={getPartiallySupportedPlatforms}
						courseId={courseId}
					/>
				)}

				{allowedCategories[currentCategory] === "Verify Identity" && (
					<VerifyIdentityOptions
						option={option}
						updateOption={updateOption}
						disabled={disabled}
						shouldShowOption={shouldShowOption}
						isOptionAllowed={isOptionAllowed}
						getPartiallySupportedPlatforms={getPartiallySupportedPlatforms}
					/>
				)}

				{allowedCategories[currentCategory] === "Tabs" && (
					<TabsOptions
						option={option}
						updateOption={updateOption}
						disabled={disabled}
						shouldShowOption={shouldShowOption}
						isOptionAllowed={isOptionAllowed}
						getPartiallySupportedPlatforms={getPartiallySupportedPlatforms}
					/>
				)}

				{allowedCategories[currentCategory] === "Disable Features" && (
					<DisableFeaturesOptions
						option={option}
						updateOption={updateOption}
						disabled={disabled}
						shouldShowOption={shouldShowOption}
						isOptionAllowed={isOptionAllowed}
						hasPassword={hasPassword}
						fetchChromeExtensionData={fetchChromeExtensionData}
						extensions={extensions}
						getPartiallySupportedPlatforms={getPartiallySupportedPlatforms}
					/>
				)}

				{allowedCategories[currentCategory] === "Restrict Applications" && (
					<BlockItemsOptions
						option={option}
						updateOption={updateOption}
						disabled={disabled}
						shouldShowOption={shouldShowOption}
						isOptionAllowed={isOptionAllowed}
						applications={applications}
						getPartiallySupportedPlatforms={getPartiallySupportedPlatforms}
					/>
				)}

				{allowedCategories[currentCategory] === "Show Tools" && (
					<ShowToolsOptions
						option={option}
						updateOption={updateOption}
						disabled={disabled}
						shouldShowOption={shouldShowOption}
						isOptionAllowed={isOptionAllowed}
						getPartiallySupportedPlatforms={getPartiallySupportedPlatforms}
					/>
				)}
			</div>
		</div>
	);
}

export default ProctoringOptions;
